import React, { useState } from 'react';
import {
  checkReturnEligibility,
  createReturnCorrect,
} from '../../services/returnService';
import { ReturnCheckResponse } from '../../types/return';

interface CreateReturnModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const CreateReturnModal: React.FC<CreateReturnModalProps> = ({
  onClose,
  onSuccess,
}) => {
  // Step 1: Check eligibility
  const [invoiceNo, setInvoiceNo] = useState('');
  const [itemId, setItemId] = useState<number | ''>('');
  const [checkResult, setCheckResult] = useState<ReturnCheckResponse | null>(null);
  const [checking, setChecking] = useState(false);

  // Step 2: Create return
  const [qty, setQty] = useState(1);
  const [returnType, setReturnType] = useState<'refund' | 'exchange'>('refund');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCheck = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!invoiceNo || !itemId) {
      alert('Lengkapi Invoice No dan Item ID');
      return;
    }

    setChecking(true);
    try {
      const result = await checkReturnEligibility({
        invoice_no: invoiceNo,
        item_id: itemId as number,
      });

      if (!result.eligible) {
        alert(`❌ Tidak bisa retur:\n${result.reason}`);
        setCheckResult(null);
      } else {
        setCheckResult(result);
        setQty(1); // Default qty
      }
    } catch (error: any) {
      alert(
        `Error check eligibility: ${
          error.response?.data?.detail || error.message
        }`
      );
      setCheckResult(null);
    } finally {
      setChecking(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!checkResult?.sale_item_id) {
      alert('Check eligibility dulu!');
      return;
    }

    if (!reason.trim()) {
      alert('Alasan retur harus diisi');
      return;
    }

    setLoading(true);
    try {
      await createReturnCorrect({
        sale_item_id: checkResult.sale_item_id, // ✅ Pakai sale_item_id
        qty,
        return_type: returnType,
        reason,
      });

      alert('✅ Retur berhasil dibuat!\nStatus: PENDING (menunggu approval)');
      onSuccess();
    } catch (error: any) {
      alert(`Error: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold">Buat Retur Barang</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="p-6">
          {/* STEP 1: Check Eligibility */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded">
            <h3 className="font-semibold mb-3">Step 1: Cek Kelayakan Retur</h3>
            <form onSubmit={handleCheck} className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Invoice No *
                </label>
                <input
                  type="text"
                  value={invoiceNo}
                  onChange={(e) => setInvoiceNo(e.target.value.toUpperCase())}
                  placeholder="INV-20260219-0001"
                  className="w-full px-3 py-2 border rounded"
                  required
                  disabled={!!checkResult}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Item ID *
                </label>
                <input
                  type="number"
                  value={itemId}
                  onChange={(e) =>
                    setItemId(e.target.value ? parseInt(e.target.value) : '')
                  }
                  placeholder="39"
                  className="w-full px-3 py-2 border rounded"
                  required
                  disabled={!!checkResult}
                />
                <p className="text-xs text-gray-500 mt-1">
                  ID barang dari master inventory (bukan SKU)
                </p>
              </div>

              {!checkResult && (
                <button
                  type="submit"
                  disabled={checking}
                  className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:bg-gray-300"
                >
                  {checking ? 'Mengecek...' : '🔍 Cek Kelayakan'}
                </button>
              )}
            </form>
          </div>

          {/* Eligibility Result */}
          {checkResult && checkResult.eligible && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded">
              <h4 className="font-semibold text-green-800 mb-2">
                ✅ Item Layak untuk Retur
              </h4>
              <div className="text-sm space-y-1">
                <p>
                  <strong>Item:</strong> {checkResult.item_name} (
                  {checkResult.item_sku})
                </p>
                <p>
                  <strong>Qty Terjual:</strong> {checkResult.qty_sold} pcs
                </p>
                <p>
                  <strong>Garansi:</strong> {checkResult.warranty_days} hari
                </p>
                <p>
                  <strong>Sisa Garansi:</strong> {checkResult.days_remaining}{' '}
                  hari
                </p>
                <p>
                  <strong>Expired:</strong>{' '}
                  {checkResult.warranty_expired_at
                    ? new Date(
                        checkResult.warranty_expired_at
                      ).toLocaleDateString('id-ID')
                    : '-'}
                </p>
              </div>
              <button
                onClick={() => {
                  setCheckResult(null);
                  setInvoiceNo('');
                  setItemId('');
                  setQty(1);
                  setReason('');
                }}
                className="mt-3 text-sm text-blue-600 hover:underline"
              >
                🔄 Cek item lain
              </button>
            </div>
          )}

          {/* STEP 2: Create Return Form */}
          {checkResult && checkResult.eligible && (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded">
              <h3 className="font-semibold mb-3">Step 2: Detail Retur</h3>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Quantity Retur *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max={checkResult.qty_sold}
                    value={qty}
                    onChange={(e) => setQty(parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 border rounded"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Maksimal: {checkResult.qty_sold} pcs
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Jenis Retur *
                  </label>
                  <select
                    value={returnType}
                    onChange={(e) => setReturnType(e.target.value as any)}
                    className="w-full px-3 py-2 border rounded"
                    required
                  >
                    <option value="refund">Refund (Uang Kembali)</option>
                    <option value="exchange">Exchange (Tukar Barang)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Alasan Retur *
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="LCD shadow / Batre drop / dll"
                    className="w-full px-3 py-2 border rounded h-24"
                    required
                  />
                </div>

                <div className="flex gap-3 pt-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:bg-gray-300"
                  >
                    {loading ? 'Memproses...' : '✅ Buat Retur'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateReturnModal;
